import React from 'react';
import { Button, Header3Text, PageLoading } from 'ui-toolkit';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { StyledFeeSection, StyledFeeSectionHeader } from '../FeeSections.Styled';
import { IncomingACHTable } from './IncomingACHTable';
import { IncomingAchDepositValidationSchema } from './IncomingAchDepositValidationSchema';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';
import {
  useCreateACHDepositMutation,
  useUpdateACHDepositMutation,
  useDeleteACHDepositMutation,
  useSendACHDepositMutation,
} from 'API/order/escrow/EscrowBaseAPI';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { StyledIncomingFlexRow } from './StyledIncomingFlexRow';
import { isNil } from 'lodash';

interface IIncomingACHSection {
  achDeposits: IEscrowExpectedIncomingAchData[];
  orderId: string;
  escrowFileId: string;
}

export const IncomingACHSection: React.FC<IIncomingACHSection> = ({ orderId, escrowFileId, achDeposits }) => {
  const [onCreate, { isLoading: isCreating }] = useCreateACHDepositMutation();
  const [onUpdate, { isLoading: isUpdating }] = useUpdateACHDepositMutation();
  const [onDelete, { isLoading: isDeleting }] = useDeleteACHDepositMutation();
  const [onSend, { isLoading: isSending }] = useSendACHDepositMutation();

  const onSave = async (
    values: { achDeposits: IEscrowExpectedIncomingAchData[] },
    { setFieldValue }: FormikHelpers<any>
  ) => {
    values.achDeposits.forEach(async (deposit, index) => {
      if (!deposit.isEditing) {
        return;
      }
      const { achAccount, version, ...rest } = deposit;
      if (deposit.createdAt) {
        const updateResult = await onUpdate({
          ...rest,
          orderId,
          escrowFileId,
          achAccountId: achAccount?.id,
        });
        if (checkForMutationSuccess(updateResult, 'updating ach')) {
          setFieldValue(`achDeposits.${index}.isEditing`, false);
        }
      } else {
        const result = await onCreate({
          ...rest,
          orderId,
          escrowFileId,
          version,
        });
        if (checkForMutationSuccess(result, 'creating ach')) {
          setFieldValue(`achDeposits.${index}.isEditing`, false);
        }
      }
    });
  };

  const initialValues =
    achDeposits.filter(({ enqueuedForDisbursementAt }: IEscrowExpectedIncomingAchData) =>
      isNil(enqueuedForDisbursementAt)
    ) || [];

  return (
    <StyledFeeSection>
      <PageLoading isLoading={isCreating || isUpdating || isDeleting || isSending} />
      <StyledFeeSectionHeader>
        <Header3Text>Incoming ACH</Header3Text>
      </StyledFeeSectionHeader>
      <Formik
        initialValues={{ achDeposits: initialValues }}
        validationSchema={IncomingAchDepositValidationSchema}
        onSubmit={onSave}
        enableReinitialize
      >
        {({ values, handleSubmit }) => (
          <>
            <Form onSubmit={handleSubmit}>
              <FieldArray name={'achDeposits'}>
                {({ push, remove }) => (
                  <>
                    <IncomingACHTable
                      onDelete={(id) => onDelete({ id, orderId })}
                      onSend={(id) => onSend({ id, orderId })}
                      remove={remove}
                    />
                    <StyledIncomingFlexRow>
                      <Button
                        intent={'primary'}
                        onClick={() => push({ version: 'v2-fees', isEditing: true })}
                        data-test-id={'add-ach-button'}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add ACH
                      </Button>
                      {values.achDeposits.length > 0 && (
                        <Button intent={'secondary'} type={'submit'} data-test-id={'update-ach-button'}>
                          Update Escrow File
                        </Button>
                      )}
                    </StyledIncomingFlexRow>
                  </>
                )}
              </FieldArray>
            </Form>
          </>
        )}
      </Formik>
    </StyledFeeSection>
  );
};
